import qs from 'qs';
import snakeize from 'snakeize';
import {
  fetchRequest,
  genOptions,
  getJson,
  isSuccessful,
  route,
} from './utils/api';

const { REACT_APP_STRIPE_API_KEY } = process.env;

const stripeHeaders = {
  'Content-Type': 'application/x-www-form-urlencoded',
  Authorization: `BEARER ${REACT_APP_STRIPE_API_KEY}`,
};

export function getDevice(imei) {
  const url = route(`/devices/${imei}`);
  const options = genOptions('GET', null);

  return fetchRequest(url, options, 'get-device');
}

export function getDevices(groupSlug) {
  const url = route(
    `/products.json?supported=true&limit=1000${
      groupSlug ? '&group=' + groupSlug : ''
    }`
  );
  const options = genOptions('GET', null);

  return fetchRequest(url, options, 'get-devices');
}

export function getIssues(groupSlug, productId) {
  const url = route(
    `/issues.json${groupSlug ? '?group=' + groupSlug : ''}${'&product_id=' +
      productId}`
  );
  const options = genOptions('GET');

  return fetchRequest(url, options, 'get-issues');
}

export function createRepair(repair) {
  const url = route('/web_repairs');
  const options = genOptions('POST', snakeize(repair));

  return fetchRequest(url, options, 'create-repair');
}

export function cancelRepair(repairId) {
  const url = route(`/web_repairs/${repairId}/cancel`);
  const options = genOptions('PATCH');

  return fetchRequest(url, options, 'cancel-repair');
}

export function rescheduleRepair(id, start, end) {
  const url = route(`/web_repairs/${id}/reschedule`);
  const options = genOptions('PATCH', {
    scheduled_window_start: start,
    scheduled_window_end: end,
  });

  return fetchRequest(url, options, 'reschedule-repair');
}

export function getRepair(id) {
  const url = route(`/claims/${id}`);
  const options = genOptions('GET');

  return fetchRequest(url, options, 'get-repair');
}

export function getServiceDisclaimer(id) {
  const url = route(`/groups/${id}/service_disclaimer.json`);
  const options = genOptions('GET');

  return fetchRequest(url, options, 'get-service-disclaimer');
}

export function getBatch(id) {
  const url = route(`/repair_batches/${id}`);
  const options = genOptions('GET');

  return fetchRequest(url, options);
}

export function getSchedulableWindows({
  certificationIds: certification_ids,
  groupId: group_id,
  latitude,
  longitude,
  productIds: product_ids,
  target,
  timeZone: time_zone,
  neededDays: needed_days,
  claimId: claim_id,
  storeId: store_id,
}) {
  const query = qs.stringify({
    certification_ids,
    group_id,
    latitude,
    longitude,
    product_ids,
    target,
    time_zone,
    needed_days,
    claim_id,
    store_id,
  });
  const url = route(`/schedule/calculate?${query}`);
  const options = genOptions('GET');

  return fetchRequest(url, options, 'get-schedulable-windows');
}

export function validateScheduledWindow({
  certificationIds: certification_ids,
  groupId: group_id,
  start,
  end,
  latitude,
  longitude,
  productIds: product_ids,
  timeZone: time_zone,
}) {
  const query = qs.stringify({
    certification_ids,
    group_id,
    start,
    end,
    latitude,
    longitude,
    product_ids,
    time_zone,
  });
  const url = route(`/schedule/validate?${query}`);
  const options = genOptions('GET');

  return fetchRequest(url, options, 'validate-schedulable-windows');
}

export function saveCancellationComment(id, comment) {
  const url = route(`/web_repairs/cancel/comment/${id}`);
  const options = genOptions('POST', { comment: comment });

  return fetchRequest(url, options, 'save-cancellation-comment');
}

export function saveReview(repairId, review) {
  const url = route('/claims/rating');
  const options = genOptions('POST', {
    claim_rating: {
      claim_id: repairId,
      tech_rating: review.techRating,
      fixt_rating: review.fixtRating,
      comment: review.comment,
    },
  });

  return fetchRequest(url, options, 'save-review');
}

export function savePostRequestIssue(repairId, note) {
  const url = route(`/web_repairs/${repairId}/post_request_issue`);
  const options = genOptions('POST', { note: note });
  return fetchRequest(url, options, 'savePostRequestIssue');
}

export function getGroup({ id, asSlug }) {
  let url;
  if (asSlug) {
    url = route(`/groups/slug.json?slug=${id}`);
  } else {
    url = route(`/groups/slug.json?id=${id}`);
  }
  const options = genOptions('GET');

  return fetchRequest(url, options, 'get-group');
}

export function getPrices({ product, group, issues }) {
  //Short circuit for a refresh when past the Issue Component due to Parent/Child rendering order with the layout
  if (!product.id) {
    let err = new Error('Missing Product.id');
    console.warn(err.message);
    return Promise.reject(err);
  }

  const issuesQuery = qs.stringify(
    { issues: snakeize(issues) },
    { arrayFormat: 'brackets' }
  );
  const query = `product_id=${product.id}&group_id=${group.id}&${issuesQuery}`;
  const url = route(`/prices.json?${query}`);

  const options = genOptions('GET');

  return fetchRequest(url, options, 'get-prices');
}

export function createStripeToken(card) {
  const url = `https://api.stripe.com/v1/tokens?${qs.stringify({
    card: card,
  })}`;

  const options = genOptions('POST', card, stripeHeaders);

  return fetch(url, options, 'create-stripe-token')
    .then(isSuccessful)
    .then(getJson);
}

export function createStripeCustomer(data) {
  const stripeCustomerHost = process.env.REACT_APP_STRIPE_CUSTOMER_ROUTE;
  const url = route(
    `${stripeCustomerHost}?${qs.stringify({ stripeToken: data })}`
  );

  const options = genOptions('POST', data, stripeHeaders);

  return fetchRequest(url, options, 'create-stripe-customer');
}

export async function getDeviceTypes() {
  const url = route('/device_types_list');
  const options = genOptions('GET');

  return fetchRequest(url, options, 'get-device-types');
}

export async function getServiceOptions({
  lat: latitude,
  lng: longitude,
  groupId: group_id,
  googleAddressComponents: google_address_components,
  repairId: repair_id,
  color = 'unknown',
  issueName: issue_name,
  productIds: product_ids,
  carrier,
  capacity,
  imei,
  phoneNumber: phone_number,
}) {
  const query = qs.stringify({
    latitude,
    longitude,
    group_id,
    repair_id,
    color,
    issue_name,
    product_ids,
    carrier,
    capacity,
    imei,
    phone_number,
    google_address_components,
  });
  const url = route(`/service_options?${query}`);
  const options = genOptions('GET');
  return fetchRequest(url, options, 'get-service-options');
}

export async function getNearbyStores({
  latitude,
  longitude,
  productIds: product_ids,
  groupId: group_id,
  repairId: repair_id,
  color = 'unknown',
  issueName: issue_name,
  carrier,
  capacity,
  imei,
  phoneNumber: phone_number,
  googleAddressComponents: google_address_components,
}) {
  const query = qs.stringify({
    latitude,
    longitude,
    group_id,
    repair_id,
    color,
    issue_name,
    product_ids,
    carrier,
    capacity,
    imei,
    phone_number,
    google_address_components,
  });
  const url = route(`/nearby_stores?${query}`);

  const options = genOptions('GET');
  return fetchRequest(url, options, 'get-nearby-stores');
}

export async function getCustomerDetails(msisdn, groupId) {
  const url = route(
    `/customer_details?msisdn=${encodeURIComponent(
      msisdn
    )}&group_id=${encodeURIComponent(groupId)}`
  );
  const options = genOptions('GET');
  return fetchRequest(url, options, 'get-customer-details');
}

export async function updateSelectedStore(id, store) {
  const url = route(`/web_repairs/${id}/update_selected_store`);
  const options = genOptions('PATCH', {
    store,
  });

  return fetchRequest(url, options, 'update-selected-store');
}

export async function getSettings() {
  const url = route(`/hq_settings`);
  const options = genOptions('GET');
  return fetchRequest(url, options, 'get-settings');
}

export async function getDynamicContent(slug) {
  const url = route(`/group_dynamic_content?slug=${slug}`);
  const options = genOptions('GET');
  return fetchRequest(url, options, 'get-dynamic-content');
}

export async function getPageText() {
  const url = route('/page_text');
  const options = genOptions('GET');
  return fetchRequest(url, options, 'get-page-text');
}
